import {get, isEmpty} from "lodash"
import {multipartConfig} from 'services/api'
import icons from "theme/icons"
import {formatRoundedNumber} from "helpers/financial"

export const getDocumentSize = (bytes, decimals) => {
  if (bytes === 0) return '0 Byte'
  const k = 1000 // or 1024 for binary
  const dm = decimals + 1 || 3
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat(formatRoundedNumber((bytes / Math.pow(k, i)), dm)) + ' ' + sizes[i]
}

export const convertMimeToExtension = {
  'audio/aac': 'AAC audio',
  'application/x-abiword': 'AbiWord document',
  'application/x-freearc': 'Archive document',
  'image/avif': 'AVIF image',
  'video/x-msvideo': 'AVI: Audio Video Interleave',
  'application/vnd.amazon.ebook': 'Amazon Kindle eBook format',
  'application/octet-stream': 'Binary data',
  'image/bmp': 'Windows OS/2 Bitmap Graphics',
  'application/x-bzip': 'BZip archive',
  'application/x-bzip2': 'BZip2 archive',
  'application/x-cdf': 'CD audio',
  'application/x-csh': 'C-Shell script',
  'text/css': 'Cascading Style Sheets (CSS)',
  'text/csv': 'Comma-separated values (CSV)',
  'application/msword': 'Microsoft Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word (OpenXML)',
  'application/vnd.ms-fontobject': 'MS Embedded OpenType fonts',
  'application/epub+zip': 'Electronic publication (EPUB)',
  'application/gzip': 'GZip Compressed Archive',
  'image/gif': 'Graphics Interchange Format (GIF)',
  'text/html': 'HyperText Markup Language (HTML)',
  'image/vnd.microsoft.icon': 'Icon format',
  'text/calendar': 'iCalendar format',
  'application/java-archive': 'Java Archive (JAR)',
  'image/jpeg': 'JPEG image',
  'text/javascript': 'JavaScript',
  'application/json': 'JSON format',
  'application/ld+json': 'JSON-LD format',
  'audio/midi': 'Musical Instrument Digital Interface (MIDI)',
  'audio/x-midi': 'Musical Instrument Digital Interface (MIDI)',
  'audio/mpeg': 'MP3 audio',
  'video/mp4': 'MP4 video',
  'video/mpeg': 'MPEG Video',
  'application/vnd.apple.installer+xml': 'Apple Installer Package',
  'application/vnd.oasis.opendocument.presentation': 'OpenDocument presentation document',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument spreadsheet document',
  'application/vnd.oasis.opendocument.text': 'OpenDocument text document',
  'audio/ogg': 'OGG audio',
  'video/ogg': 'OGG video',
  'application/ogg': 'OGG',
  'audio/opus': 'Opus audio',
  'font/otf': 'OpenType font',
  'image/png': 'PNG image',
  'application/pdf': 'Adobe Portable Document Format (PDF)',
  'application/x-httpd-php': 'Hypertext Preprocessor (Personal Home Page)',
  'application/vnd.ms-powerpoint': 'Microsoft PowerPoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Microsoft PowerPoint (OpenXML)',
  'application/vnd.rar': 'RAR archive',
  'application/rtf': 'Rich Text Format (RTF)',
  'application/x-sh': 'Bourne shell script',
  'image/svg+xml': 'Scalable Vector Graphics (SVG)',
  'application/x-tar': 'Tape Archive (TAR)',
  'image/tiff': 'Tagged Image File Format (TIFF)',
  'video/mp2t': 'MPEG transport stream',
  'font/ttf': 'TrueType Font',
  'text/plain': 'Text',
  'application/vnd.visio': 'Microsoft Visio',
  'audio/wav': 'Waveform Audio Format',
  'audio/webm': 'WEBM audio',
  'video/webm': 'WEBM video',
  'image/webp': 'WEBP image',
  'font/woff': 'Web Open Font Format (WOFF)',
  'font/woff2': 'Web Open Font Format (WOFF)',
  'application/xhtml+xml': 'XHTML',
  'application/xml': 'XML',
  'application/vnd.ms-excel': 'Microsoft Excel',
  'application/vnd.mozilla.xul+xml': 'XUL',
  'application/zip': 'ZIP archive',
  'video/3gpp': '3GPP video container',
  'audio/3gpp': '3GPP audio container',
  'video/3gpp2': '3GPP2 video container',
  'audio/3gpp2': '3GPP2 audio container',
  'application/x-7z-compressed': '7-zip archive',
}

export const getFileIcon = (documentFile) => {
  if (documentFile.isFolder === true) {
    return 'folder_filled'
  } else {
    switch (documentFile.fileExtension) {
      case 'xls':
        return 'xls'
      case 'xlsx':
        return 'xls'
      case 'xml':
        return 'xml'
      case 'ods':
        return 'file'
      case 'csv':
        return 'csv'
      case 'tmpl':
        return 'file'
      case 'pdf':
        return 'pdf'
      case 'php':
        return 'php'
      case 'png':
        return 'png'
      case 'jpg':
        return 'jpg'
      case 'gif':
        return 'gif'
      case 'bmp':
        return 'raw'
      case 'txt':
        return 'txt'
      case 'doc':
        return 'doc'
      case 'js':
        return 'js'
      case 'swf':
        return 'file'
      case 'mp3':
        return 'mp3'
      case 'zip':
        return 'zip'
      case 'rar':
        return 'rar'
      case 'tar':
        return 'tar'
      case 'arj':
        return 'file'
      case 'cab':
        return 'file'
      case 'html':
        return 'html'
      case 'htm':
        return 'html'
      case 'folder':
        return 'folder_filled'
      default:
        return 'file'
    }
  }
}

export const DocumentFolderTemplateType = {
  ORDER: 'ORDER',
  PURCHASE_ORDER: 'PURCHASE_ORDER',
}

export const getFileFormData = (files) => {
  // Create the Form data
  const formData = new FormData()
  for (let i = 0; i < files.length; i++) {
    formData.append(files[i].name, files[i])
  }
  return formData
}

export const getFileAsBytes = (files) => {
  const file = files[0]
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const arrayBuffer = reader.result
      const bytes = new Uint8Array(arrayBuffer)
      resolve(bytes)
    }
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

export const getFileFormConfig = (onUploadProgress) => {
  return {
    ...multipartConfig,
    onUploadProgress,
  }
}

export const validateFileType = (files, allowedExtensions) => {
  allowedExtensions = allowedExtensions.map(s => s.toLowerCase())
  for (let i = 0; i < files.length; i++) {
    const ext = getFileExtension(files[i].name).toLowerCase()
    if (allowedExtensions.indexOf(ext) < 0) return false
  }
  return true
}

export const getFileExtension = (fileName) => {
  const idx = fileName.lastIndexOf('.')
  if (idx >= 0) return fileName.substring(idx + 1)
  return ''
}

export const getDocumentActions = (
    onUploadSelect,
    onDeleteSelect,
    onOpenInGoogleDriveSelect,
    readOnly = false,
    isDeleteEnabled = true
) => {
  return [
    {
      title: 'Upload',
      icon: 'upload',
      onClick: onUploadSelect,
      readOnly,
    },
    {
      title: 'Delete',
      icon: icons.delete,
      disabled: !isDeleteEnabled,
      onClick: onDeleteSelect,
      readOnly,
    },
    {
      title: 'Open Folder In Google Drive',
      icon: 'google_drive',
      onClick: onOpenInGoogleDriveSelect,
      readOnly,
    },
  ]
}

export const getFileChunks = (bytes, chunkSize, fileSize) => {
  const chunks = []

  for (let i = 0; i < bytes.length; i += chunkSize) {
    const fileChunk = bytes.slice(i, i + chunkSize)
    const chunkLength = get(fileChunk, 'length')
    const range = `bytes ${i}-${i + chunkLength - 1}/${fileSize}`
    chunks.push({fileChunk, chunkLength, range})
  }

  return chunks
}

export const getCompanyDocumentsFolderPath = (company) => {
  return get(company, 'name')
}

export const getOrderDocumentsFolderPath = (order) => {
  const orderRef = get(order, 'ref')
  const company = get(order, 'company')
  const companyFolder = getCompanyDocumentsFolderPath(company)
  return `${companyFolder}/${orderRef}`
}

export const getApprovalsFolderPath = (approvalReference, approvalRevision) => {
  return `/Approvals/${approvalReference}/Revision ${approvalRevision}`
}

export const getPurchaseOrderDocumentsFolderPath = (purchaseOrder) => {
  const orderRef = get(purchaseOrder, 'reference')
  const company = get(purchaseOrder, 'company')
  const companyFolder = getCompanyDocumentsFolderPath(company)
  return `${companyFolder}/${orderRef}`
}

export const isDeleteEnabled = (documents) => {
  if (isEmpty(documents)) return false
  return !isAnyDocumentAFolder(documents)
}

export const isAnyDocumentAFolder = (documents) => {
  return documents.some(document => document.isFolder)
}

export default {
  getDocumentSize,
  getFileIcon,
  getFileFormData,
  validateFileType,
}
